import type { CollageDocument, PhotoElement, TextElement } from '../../shared/document';
import { getPhotoDrawGeometry } from '../geometry/photoCrop';
import { getBackgroundImageGeometry } from '../geometry/backgroundImage';
import { binaryAssetRegistry } from '../store/binaryAssetRegistry';
import { BUILTIN_FONT_FAMILIES } from '../../shared/schemas';
import { systemFontFamily } from '../../shared/systemFonts';
import { createTextTexture } from '../editor/textTexture';

export type ExportFormat = 'png' | 'jpeg';

function roundedRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
  context.beginPath();
  context.roundRect(x, y, width, height, Math.min(radius, width / 2, height / 2));
}

function drawText(context: CanvasRenderingContext2D, element: TextElement, document: CollageDocument, pixelRatio: number): void {
  const { cx, cy, width, height, rotation } = element.transform;
  const family = document.fonts[element.fontId]?.family ?? BUILTIN_FONT_FAMILIES[element.fontId] ?? systemFontFamily(element.fontId) ?? 'sans-serif';
  context.save();
  context.globalAlpha *= element.opacity;
  context.translate(cx, cy);
  context.rotate(rotation * Math.PI / 180);
  const texture = createTextTexture(element, family, pixelRatio);
  context.shadowColor = element.shadowColor;
  context.shadowBlur = element.shadowBlur;
  context.shadowOffsetX = element.shadowOffsetX;
  context.shadowOffsetY = element.shadowOffsetY;
  context.drawImage(texture, -width / 2, -height / 2, width, height);
  context.restore();
}

async function drawPhoto(context: CanvasRenderingContext2D, element: PhotoElement, image: ImageBitmap | null): Promise<void> {
  const { cx, cy, width, height, rotation } = element.transform;
  context.save();
  context.globalAlpha *= element.opacity;
  context.translate(cx, cy);
  context.rotate(rotation * Math.PI / 180);
  context.translate(-width / 2, -height / 2);
  roundedRect(context, 0, 0, width, height, element.frame.radius);
  const elementAlpha = context.globalAlpha;
  context.globalAlpha *= element.imageOpacity;
  context.fillStyle = element.frame.fill;
  context.shadowColor = element.shadowColor;
  context.shadowBlur = element.shadowBlur;
  context.shadowOffsetX = element.shadowOffsetX;
  context.shadowOffsetY = element.shadowOffsetY;
  context.fill();
  context.shadowColor = 'transparent';
  context.shadowBlur = 0;
  context.shadowOffsetX = 0;
  context.shadowOffsetY = 0;
  context.globalAlpha = elementAlpha;
  if (image) {
    context.save();
    context.clip();
    context.globalAlpha *= element.imageOpacity;
    context.filter = `blur(${element.imageBlur}px) brightness(${element.imageBrightness}) saturate(${element.imageSaturation})`;
    const geometry = getPhotoDrawGeometry(image.width, image.height, width, height, element);
    context.translate(geometry.x, geometry.y);
    context.rotate(geometry.rotation * Math.PI / 180);
    context.scale(geometry.scaleX, geometry.scaleY);
    context.drawImage(image, -geometry.width / 2, -geometry.height / 2, geometry.width, geometry.height);
    context.restore();
  }
  if (element.frame.borderWidth > 0) {
    context.globalAlpha *= element.imageOpacity;
    roundedRect(context, element.frame.borderWidth / 2, element.frame.borderWidth / 2, width - element.frame.borderWidth, height - element.frame.borderWidth, element.frame.radius);
    context.strokeStyle = element.frame.borderColor;
    context.lineWidth = element.frame.borderWidth;
    context.stroke();
    context.globalAlpha = elementAlpha;
  }
  context.restore();
}

export async function renderDocumentImage(
  document: CollageDocument,
  width: number,
  height: number,
  format: ExportFormat,
): Promise<Uint8Array> {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 || width > 32767 || height > 32767 || width * height > 256_000_000) {
    throw new Error('Izvoz je omejen na 32767 px na stran in 256 milijonov slikovnih pik.');
  }
  await window.document.fonts.ready;
  const canvas = window.document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Platna za izvoz ni bilo mogoče pripraviti.');
  context.scale(width / document.canvas.width, height / document.canvas.height);
  if (document.canvas.background || document.canvas.backgroundAssetId || format === 'jpeg') {
    context.fillStyle = document.canvas.background ?? '#FFFFFF';
    context.fillRect(0, 0, document.canvas.width, document.canvas.height);
  }
  const bitmaps = new Map<string, ImageBitmap>();
  try {
    const textPixelRatio = Math.max(2, width / document.canvas.width);
    const backgroundId = document.canvas.backgroundAssetId;
    const backgroundBlob = backgroundId ? binaryAssetRegistry.get(backgroundId) : undefined;
    if (backgroundId && backgroundBlob) {
      const image = await createImageBitmap(backgroundBlob);
      bitmaps.set(backgroundId, image);
      const geometry = getBackgroundImageGeometry(
        document.canvas.width,
        document.canvas.height,
        image.width,
        image.height,
        document.canvas.backgroundImageOffsetX,
        document.canvas.backgroundImageOffsetY,
      );
      context.save();
      context.globalAlpha = document.canvas.backgroundImageOpacity;
      context.filter = `blur(${document.canvas.backgroundImageBlur}px) brightness(${document.canvas.backgroundImageBrightness}) saturate(${document.canvas.backgroundImageSaturation})`;
      context.drawImage(image, geometry.x, geometry.y, geometry.width, geometry.height);
      context.restore();
    }
    for (const element of document.elements) {
      if (!element.visible) continue;
      if (element.type === 'text') drawText(context, element, document, textPixelRatio);
      else {
        const blob = element.assetId ? binaryAssetRegistry.get(element.assetId) : undefined;
        let bitmap: ImageBitmap | null = null;
        if (blob && element.assetId) {
          bitmap = bitmaps.get(element.assetId) ?? await createImageBitmap(blob);
          bitmaps.set(element.assetId, bitmap);
        }
        await drawPhoto(context, element, bitmap);
      }
    }
    const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((value) => value ? resolve(value) : reject(new Error('Slike ni bilo mogoče kodirati.')), format === 'png' ? 'image/png' : 'image/jpeg', 0.92));
    return new Uint8Array(await blob.arrayBuffer());
  } finally {
    for (const bitmap of bitmaps.values()) bitmap.close();
  }
}
